/**
 * Steps to revert temp support for vanilla lesson templates
 * 1. Remove import of LessonPage to TempLessonPage
 * 2. uncomment the import of LessonView to TempLessonView
 * 3. Delete the LessonPage.js file
 * 4. Delete the useLessonPage.js file
 * 5. Change { path: ROUTES.LESSON, component: TempLessonPage }, to { path: ROUTES.LESSON, component: LessonView },
 */

import { TEACHER_DASHBOARD_FEATURES } from 'core/chatbot/constants/teacherDashboardFeatures';
import { isTeams } from 'core/utils/teamsTab';
import { ROUTES } from 'edelvives/utils/constants/paths';
import React from 'react';

import DemoLoading from 'edelvives/pages/DemoLandingPage/DemoLoading';
// import LogoutPage from '../modules/containers/Logout';
import AboutView from '../modules/views/AboutView/AboutView';
import SupportView from '../modules/views/SupportView/SupportView';
import AIPolitics from '../pages/StaticPages/AIPolitics';
import PrivacyPolicy from '../pages/StaticPages/PrivacyPolicy';
import TermsOfUse from '../pages/StaticPages/TermsOfUse';
import TabAuthEnd from '../pages/Teams/TabAuthEnd';
import TabAuthStart from '../pages/Teams/TabAuthStart';
import LoginPage from '../pages/auth/Login/LoginPage';
import SignUpPage from '../pages/auth/SignUp/SignUpPage';

const ErrorView = React.lazy(() => import('edelvives/modules/views/ErrorView/ErrorView'));
const HomePage = React.lazy(() => import('edelvives/pages/HomePage/HomePage'));
const KanbanPage = React.lazy(() => import('edelvives/pages/KanbanPage/KanbanPage'));
const CoursePage = React.lazy(() => import('edelvives/pages/CoursePage/CoursePage'));
const LessonPage = React.lazy(() => import('edelvives/pages/LessonPage/LessonPage'));
// eslint-disable-next-line no-unused-vars
const LessonView = React.lazy(() => import('core/lite/views/LessonView/LessonView/LessonView'));
const CustomLessonView = React.lazy(() => import('edelvives/modules/views/CustomLessonView/CustomLessonView'));
const CustomLessonViewToDigest = React.lazy(() => import('edelvives/modules/views/CustomLessonView/CustomLessonViewToDigest'));
const LogoutPage = React.lazy(() => import('edelvives/modules/containers/Logout'));
const ToolsProjectContainer = React.lazy(() => import('core/lite/views/ToolsProjectView/ToolsProjectContainer'));
const AnswerTest = React.lazy(() => import('../pages/AnswerTest'));
const AssessmentResult = React.lazy(() => import('../pages/AssessmentResultPage/AssessmentResult'));
const TokenCallback = React.lazy(() => import('../pages/auth/TokenCallback'));

const CalendarPage = React.lazy(() => import('../pages/CalendarPage/CalendarPage'));
const LemonTest = React.lazy(() => import('../pages/LemonTest'));
// const NewContentView = React.lazy(() => import('../pages/NewContent'));
const ProfilePage = React.lazy(() => import('../pages/ProfilePage/ProfilePage'));
const ProjectionMint = React.lazy(() => import('../pages/ProjectionMint'));
const Projection = React.lazy(() => import('../pages/Projection'));
const RemotePage = React.lazy(() => import('../pages/RemotePage'));
const ViewerRouting = React.lazy(() => import('../containers/ViewerContainer/ViewerRouting'));
const AddLicense = React.lazy(() => import('../pages/AddLicensePage/AddLicensePage'));
const RecoverPasswordPage = React.lazy(() => import('../pages/RecoverPasswordPage/RecoverPasswordPage'));
const ClassroomUsersPage = React.lazy(() => import('../pages/ClassroomUsersPage/ClassroomUsersPage'));
const GroupResourcesPage = React.lazy(() => import('../pages/GroupResourcesPage/GroupResourcesPage'));
const MSTeamsConfiguration = React.lazy(() => import('../pages/MSTeamsConfiguration'));
const IndividualActivityPage = React.lazy(() => import('../pages/IndividualActivityPage/IndividualActivityPage'));
const DeepLinkView = React.lazy(() => import('core/lite/views/DeepLinkView'));
const DialogDesktopVersion = React.lazy(() => import('core/modules/components/dialogs/DialogDesktopVersion/DialogDesktopVersion'));
// const DemoLandingPage = React.lazy(() => import('edelvives/pages/DemoEsoLandingPage/DemoEsoLandingPage'));
const CreateEvaluationsPage = React.lazy(() => import('../pages/CreateEvaluationsPage'));
const CreateEvaluationsIAPage = React.lazy(() => import('../pages/CreateEvaluationsIAPage'));
const DoEvaluationPage = React.lazy(() => import('../pages/DoEvaluationPage'));
const ViewEvaluationPage = React.lazy(() => import('../pages/ViewEvaluationPage'));
const ResultsEvaluationPage = React.lazy(() => import('../pages/ResultsEvaluationPage'));
const ResourcesPage = React.lazy(() => import('edelvives/pages/ResourcesPage/ResourcesPage'));
const TasksPage = React.lazy(() => import('edelvives/pages/TasksPage'));
const AssessmentPage = React.lazy(() => import('edelvives/pages/AssessmentPage'));
const ResetPasswordPage = React.lazy(() => import('../pages/ResetPasswordPage/ResetPasswordPage'));
const ChatbotPracticePage = React.lazy(() => import('edelvives/pages/ChatbotPracticePage'));
const RadarPage = React.lazy(() => import('app/pages/RadarPage/RadarPage'));
const StudentKcProgressPage = React.lazy(() => import('app/pages/StudentKcProgressPage/StudentKcProgressPage'));
const StudentSkillsProgressPage = React.lazy(() => import('app/pages/StudentSkillsProgressPage/StudentSkillsProgressPage'));
const TeacherAnalyticsStudentsListPage = React.lazy(() => import('app/pages/TeacherAnalyticsStudentsListPage/TeacherAnalyticsStudentsListPage'));
const AnalyticsPage = React.lazy(() => import('app/pages/AnalyticsPage/AnalyticsPage'));
// const TeacherAnalyticsPage = React.lazy(() => import('core/chatbot/views/TeacherAnalyticsView/TeacherAnalyticsView')); // Deprecated
const TeacherToolsPage = React.lazy(() => import('core/chatbot/views/TeacherToolsView/TeacherToolsView'));
const TeacherToolsHistoryView = React.lazy(() => import('core/chatbot/views/TeacherToolsHistoryView/TeacherToolsHistoryView'));
const ToolInstanceChatView = React.lazy(() => import('core/chatbot/views/ToolInstanceChatView/ToolInstanceChatView'));
const GenericToolSetup = React.lazy(() => import('core/chatbot/components/tools/GenericToolSetup/GenericToolSetup'));

export const externalRoutes = [
  { path: ROUTES.REMOTE_TOKEN, component: RemotePage },
  { path: ROUTES.DEEPLINK, component: DeepLinkView },
  { path: ROUTES.DEEPLINK_TOKEN, component: DeepLinkView },
  { path: ROUTES.ABOUT, component: AboutView },
  { path: ROUTES.SUPPORT, component: SupportView },
  /* Teams configuration is required regardless if authorized or not to be able to bypass the popup */
  { path: ROUTES.TEAMS_CONFIG, component: MSTeamsConfiguration },
  { path: ROUTES.TEAMS_TAB_AUTH_START, component: TabAuthStart },
  { path: ROUTES.TEAMS_TAB_AUTH_END, component: TabAuthEnd },
  /* Static pages */
  { path: ROUTES.PRIVACY, component: PrivacyPolicy },
  { path: ROUTES.TERMS, component: TermsOfUse },
  { path: ROUTES.IA, component: AIPolitics },
  { path: ROUTES.LOGOUT, component: LogoutPage },
  { path: ROUTES.LOADING_DEMO, component: DemoLoading },
  { path: ROUTES.PRESCHOOL_RESOURCES, component: ResourcesPage },
];

export const publicRoutes = [
  { path: ROUTES.AUTH_LOGIN_TOKEN, component: TokenCallback },
  { path: ROUTES.AUTH_LOGIN, component: LoginPage },
  { path: ROUTES.AUTH_SIGNUP, component: SignUpPage },
  { path: ROUTES.RECOVER_PASSWORD, component: RecoverPasswordPage },
];

export const localhostRoutes = [
  // { path: ROUTES.TEST_COMPONENT, component: TestComponentView },
];

export const privateRoutes = [
  { path: ROUTES.HOME, component: HomePage },
  { path: ROUTES.ASSESSMENT_QUESTION, component: LemonTest },
  { path: ROUTES.ASSESSMENT_RESULT, component: AssessmentResult },
  { path: ROUTES.ASSESSMENT, component: AssessmentPage },
  // { path: ROUTES.NEW_CONTENT, component: NewContentView },
  // { path: ROUTES.CONTENT, component: NewContentView },
  { path: ROUTES.PROJECTION_MINT, component: ProjectionMint },
  { path: ROUTES.PROJECTION, component: Projection },
  { path: ROUTES.LESSON, component: LessonPage },
  { path: ROUTES.CUSTOM_LESSON_DIGEST, component: CustomLessonViewToDigest, props: { typeToFilterLesson: isTeams() ? 'mint' : null } },
  { path: ROUTES.CUSTOM_LESSON, component: CustomLessonView, props: { typeToFilterLesson: isTeams() ? 'mint' : null } },
  { path: ROUTES.CUSTOM_LESSON_UNIT, component: CustomLessonView, props: { typeToFilterLesson: isTeams() ? 'mint' : null } },
  { path: ROUTES.CREATE_EVALUATION, component: CreateEvaluationsPage },
  { path: ROUTES.CREATE_EVALUATION_IA, component: CreateEvaluationsIAPage },
  { path: ROUTES.EDIT_EVALUATION, component: CreateEvaluationsPage },
  { path: ROUTES.EDIT_EVALUATION_IA, component: CreateEvaluationsIAPage },
  { path: ROUTES.EVALUATION, component: DoEvaluationPage },
  { path: ROUTES.EVALUATION_VIEW_RESOURCES, component: ViewEvaluationPage },
  { path: ROUTES.EVALUATION_VIEW, component: ViewEvaluationPage },
  { path: ROUTES.EVALUATION_RESULTS_RESOURCES, component: ResultsEvaluationPage },
  { path: ROUTES.EVALUATION_RESULTS, component: ResultsEvaluationPage },
  { path: ROUTES.KANBAN_LESSON, component: KanbanPage },
  { path: ROUTES.COURSE_GRADES, component: CoursePage, props: { tabId: 'grades' } },
  { path: ROUTES.COURSE_PROGRAM, component: CoursePage, props: { tabId: 'program' } },
  { path: ROUTES.COURSE_HOME, component: CoursePage, props: { tabId: 'home' } },
  { path: ROUTES.RESOURCES_PAGE, component: GroupResourcesPage },
  { path: ROUTES.COURSE_RESOURCES, component: CoursePage, props: { tabId: 'resources' } },
  { path: ROUTES.COURSE_RESOURCES_DEMO, component: CoursePage, props: { tabId: 'resources-demo' } },
  { path: ROUTES.COURSE, component: CoursePage, props: { tabId: 'stream' } },
  { path: ROUTES.VIEWER_FULL, component: ViewerRouting, props: { showHeader: false } },
  { path: ROUTES.VIEWER, component: ViewerRouting, props: { showHeader: true } },
  { path: ROUTES.VIEWER_FULL_GROUP, component: ViewerRouting, props: { showHeader: false } },
  { path: ROUTES.VIEWER_GROUP, component: ViewerRouting, props: { showHeader: true } },
  { path: ROUTES.CALENDAR, component: CalendarPage },
  { path: ROUTES.CALENDAR_COURSE, component: CalendarPage },
  { path: ROUTES.PROFILE, component: ProfilePage },
  { path: ROUTES.TASKS, component: TasksPage },
  { path: ROUTES.LEMON_TEST, component: LemonTest },
  { path: ROUTES.ANSWER_TEST, component: AnswerTest },
  { path: ROUTES.SINGLE_ACTIVITY_RECORD, component: IndividualActivityPage, props: { record: true } },
  { path: ROUTES.SINGLE_ACTIVITY, component: IndividualActivityPage },
  { path: ROUTES.CLASSROOM_USERS, component: ClassroomUsersPage },
  { path: ROUTES.TOOLS, component: ToolsProjectContainer },
  { path: ROUTES.REMOTE, component: RemotePage },
  { path: ROUTES.ERROR, component: ErrorView },
  { path: ROUTES.LICENSE, component: AddLicense },
  { path: ROUTES.DESKTOP, component: DialogDesktopVersion },
  { path: ROUTES.RESET_PASSWORD, component: ResetPasswordPage },
  { path: ROUTES.CHATBOT_PRACTICE, component: ChatbotPracticePage },
  { path: ROUTES.CHATBOT_PRACTICE_SESSION_SUMMARY, component: ChatbotPracticePage },
  { path: ROUTES.CHATBOT_PRACTICE_SESSION_ACTIVITY, component: ChatbotPracticePage },
  { path: ROUTES.STUDENT_KC_PROGRESS, component: StudentKcProgressPage, feature: TEACHER_DASHBOARD_FEATURES.RADAR },
  { path: ROUTES.STUDENT_SKILLS_PROGRESS, component: StudentSkillsProgressPage, feature: TEACHER_DASHBOARD_FEATURES.RADAR },
  { path: ROUTES.STUDENTS_LIST, component: TeacherAnalyticsStudentsListPage, feature: TEACHER_DASHBOARD_FEATURES.RADAR },
  { path: ROUTES.RADAR, component: RadarPage, feature: TEACHER_DASHBOARD_FEATURES.RADAR },
  // { path: ROUTES.TEACHER_ANALYTICS, component: TeacherAnalyticsPage }, // Deprecated
  { path: ROUTES.TEACHER_ANALYTICS_BASE, component: AnalyticsPage, feature: TEACHER_DASHBOARD_FEATURES.ANALYTICS_PANEL },
  { path: ROUTES.TEACHER_TOOLS_HISTORY, component: TeacherToolsHistoryView, feature: TEACHER_DASHBOARD_FEATURES.AI_TOOLS },

  // Use the Chat-based split view for tool instances
  { path: ROUTES.TEACHER_TOOL_INSTANCE, component: ToolInstanceChatView, feature: TEACHER_DASHBOARD_FEATURES.AI_TOOLS },

  // Teacher Tools Setup Routes - Single parameterized route for all tools
  {
    path: '/teacher-tools/setup/:toolId',
    component: GenericToolSetup,
    feature: TEACHER_DASHBOARD_FEATURES.AI_TOOLS,
  },

  // Teacher Tools Main Routes
  { path: ROUTES.TEACHER_TOOLS, exact: true, component: TeacherToolsPage, feature: TEACHER_DASHBOARD_FEATURES.AI_TOOLS },
];
